import { downloadFile } from '@/utils';
import { message } from 'antd';
import {
  downloadExportFile,
  EXPORT_TASK_STATUS,
  EXPORT_TASK_TYPE,
  ExportTaskItem,
  getExportTaskMessage,
  getMyExportTasks,
} from '@/services/obas-api/exportTask';
import {
  buildOrderBomExportFileName,
  buildOrderListExportFileName,
  getExportFileTimeSuffix,
} from '@/utils/exportFileName';

const POLL_INTERVAL_MS = 5000;
const MAX_CONSECUTIVE_ERRORS = 5;

let polling = false;
let tickInFlight = false;
let restartRequested = false;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let consecutiveErrors = 0;
const downloadingIds = new Set<number>();
const messagingIds = new Set<number>();

/** 反复 decode，直到不再是 URL 编码 */
const decodeFileName = (name?: string | null): string | undefined => {
  if (!name) return undefined;
  let current = String(name).trim().replace(/^["']|["']$/g, '');
  if (!current) return undefined;

  // 取路径最后一段，避免 fileName 带目录
  const slashIdx = Math.max(
    current.lastIndexOf('/'),
    current.lastIndexOf('\\'),
  );
  if (slashIdx >= 0) {
    current = current.slice(slashIdx + 1);
  }

  for (let i = 0; i < 3; i += 1) {
    if (!/%[0-9a-fA-F]{2}/.test(current)) break;
    try {
      const decoded = decodeURIComponent(current.replace(/\+/g, '%20'));
      if (decoded === current) break;
      current = decoded;
    } catch {
      break;
    }
  }

  // 仍含编码则视为无效，交给调用方走兜底中文名
  if (/%[0-9a-fA-F]{2}/.test(current)) {
    return undefined;
  }

  return current;
};

const parseFileNameFromDisposition = (
  contentDisposition?: string | string[],
): string | undefined => {
  if (!contentDisposition) return undefined;
  const raw = Array.isArray(contentDisposition)
    ? contentDisposition.join('; ')
    : String(contentDisposition);

  const utf8Match = raw.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeFileName(utf8Match[1]);
  }

  const plainMatch = raw.match(/filename\s*=\s*("?)([^";]+)\1/i);
  return decodeFileName(plainMatch?.[2]);
};

const getTaskType = (task: ExportTaskItem) =>
  String(task.taskType ?? task.TaskType ?? '');

const resolveTaskExportFileName = (
  task: ExportTaskItem,
  headerFileName?: string,
) => {
  const taskType = getTaskType(task);
  const id = Number(task.id ?? task.Id);
  const rawNameHint = String(
    headerFileName || task.fileName || task.FileName || '',
  );

  // 优先按任务类型生成可读中文名 + 当前时间，避免后端编码文件名乱码
  if (taskType === EXPORT_TASK_TYPE.OrderList) {
    return buildOrderListExportFileName();
  }
  if (taskType === EXPORT_TASK_TYPE.OrderBomComparison) {
    return buildOrderBomExportFileName();
  }

  // taskType 缺失时，用原始文件名特征推断（编码串里仍保留 ASCII 的 Bom）
  if (/Bom|比对|Comparison/i.test(rawNameHint)) {
    return buildOrderBomExportFileName();
  }
  if (/信息|OrderList|List/i.test(rawNameHint)) {
    return buildOrderListExportFileName();
  }

  const decoded =
    decodeFileName(headerFileName) ||
    decodeFileName(task.fileName) ||
    decodeFileName(task.FileName);

  if (decoded) {
    const base = decoded.replace(/\.xlsx$/i, '');
    return `${base}_${getExportFileTimeSuffix()}.xlsx`;
  }

  return `导出文件_${id || 'unknown'}_${getExportFileTimeSuffix()}.xlsx`;
};

export const normalizeTaskList = (response: any): ExportTaskItem[] => {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data as ExportTaskItem[];
  if (Array.isArray(data?.items)) return data.items as ExportTaskItem[];
  if (Array.isArray(data?.list)) return data.list as ExportTaskItem[];
  return [];
};

export const getTaskId = (task: ExportTaskItem) => Number(task.id ?? task.Id);
export const getTaskStatus = (task: ExportTaskItem) =>
  Number(task.status ?? task.Status);

/** 是否存在需要继续轮询的任务：排队中 / 生成中 / 已完成待处理 */
export const shouldContinuePolling = (tasks: ExportTaskItem[]) =>
  tasks.some((task) => {
    const status = getTaskStatus(task);
    return (
      status === EXPORT_TASK_STATUS.Queued ||
      status === EXPORT_TASK_STATUS.Generating ||
      status === EXPORT_TASK_STATUS.Completed
    );
  });

const clearPollTimer = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

const scheduleNextPoll = () => {
  clearPollTimer();
  pollTimer = setTimeout(() => {
    void runPollTick();
  }, POLL_INTERVAL_MS);
};

const toBlob = (data: any): Blob | null => {
  if (data instanceof Blob) return data;
  if (data instanceof ArrayBuffer) {
    return new Blob([data]);
  }
  if (data?.data instanceof Blob) return data.data;
  return null;
};

/** 从 GetExportTaskMessage 的 IServiceResult 中解析提示文案 */
const resolveTaskMessage = (response: any): string => {
  const payload = response?.data ?? response;
  // data 为提示字符串时优先使用
  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim();
  }
  if (typeof payload?.data === 'string' && payload.data.trim()) {
    return payload.data.trim();
  }
  // swagger：提示在 IServiceResult.message
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message.trim();
  }
  if (typeof response?.message === 'string' && response.message.trim()) {
    return response.message.trim();
  }
  return '';
};

/** OrderCheck：拉取任务提示信息并展示，不下载文件 */
const notifyCompletedOrderCheckTask = async (task: ExportTaskItem) => {
  const id = getTaskId(task);
  if (!id || Number.isNaN(id) || messagingIds.has(id)) return;

  messagingIds.add(id);
  try {
    const response = await getExportTaskMessage(id);
    const tip =
      resolveTaskMessage(response) || '批量校对订单任务已执行完成';
    message.info(tip);
  } finally {
    messagingIds.delete(id);
  }
};

const downloadCompletedTask = async (task: ExportTaskItem) => {
  const id = getTaskId(task);
  if (!id || Number.isNaN(id) || downloadingIds.has(id)) return;

  downloadingIds.add(id);
  try {
    const response = await downloadExportFile(id);
    const blob = toBlob(response?.data) || toBlob(response);
    const headers =
      response?.headers ||
      (response as any)?.response?.headers ||
      {};
    const disposition =
      headers['content-disposition'] || headers['Content-Disposition'];
    const headerFileName = parseFileNameFromDisposition(disposition);
    const fileName = resolveTaskExportFileName(task, headerFileName);

    if (blob) {
      downloadFile(blob, fileName);
    } else {
      throw new Error(`导出文件为空: taskId=${id}`);
    }
  } finally {
    downloadingIds.delete(id);
  }
};

const finishTick = (shouldContinue: boolean) => {
  tickInFlight = false;

  if (restartRequested) {
    restartRequested = false;
    polling = true;
    void runPollTick();
    return;
  }

  if (shouldContinue) {
    scheduleNextPoll();
    return;
  }

  polling = false;
  clearPollTimer();
};

const runPollTick = async () => {
  tickInFlight = true;

  try {
    const response = await getMyExportTasks();
    const tasks = normalizeTaskList(response);
    consecutiveErrors = 0;

    const completedTasks = tasks.filter(
      (task) => getTaskStatus(task) === EXPORT_TASK_STATUS.Completed,
    );

    let handleFailed = false;
    for (const task of completedTasks) {
      try {
        if (getTaskType(task) === EXPORT_TASK_TYPE.OrderCheck) {
          await notifyCompletedOrderCheckTask(task);
        } else {
          await downloadCompletedTask(task);
        }
      } catch (error) {
        console.error('导出/校对任务完成后处理失败:', error);
        handleFailed = true;
      }
    }

    const hasInProgress = tasks.some((task) => {
      const status = getTaskStatus(task);
      return (
        status === EXPORT_TASK_STATUS.Queued ||
        status === EXPORT_TASK_STATUS.Generating
      );
    });

    finishTick(hasInProgress || handleFailed);
  } catch (error) {
    console.error('查询导出任务失败:', error);
    consecutiveErrors += 1;

    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS && !restartRequested) {
      tickInFlight = false;
      polling = false;
      clearPollTimer();
      return;
    }

    finishTick(consecutiveErrors < MAX_CONSECUTIVE_ERRORS);
  }
};

/**
 * 启动导出任务轮询（单例）。
 * 登录后 / CreateExportTask 成功后调用。
 */
export const startExportTaskPolling = () => {
  consecutiveErrors = 0;
  polling = true;

  if (tickInFlight) {
    restartRequested = true;
    return;
  }

  clearPollTimer();
  void runPollTick();
};

export const stopExportTaskPolling = () => {
  polling = false;
  restartRequested = false;
  clearPollTimer();
};
